'use client';

// @boty/ui - BotyThemeProvider component
// Main theme provider with RTL support using Emotion cache

import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import rtlPlugin from 'stylis-plugin-rtl';
import { prefixer } from 'stylis';
import { createBotyTheme, type Direction } from '../theme';

export interface BotyThemeProviderProps {
  children: ReactNode;
  direction?: Direction;
}

// Create LTR cache (default)
const cacheLtr = createCache({
  key: 'mui',
  prepend: true,
});

// Create RTL cache with stylis-plugin-rtl
const cacheRtl = createCache({
  key: 'muirtl',
  stylisPlugins: [prefixer, rtlPlugin],
  prepend: true,
});

/**
 * BotyThemeProvider - Wraps the app with MUI theme and RTL support
 *
 * @param children - React children to wrap
 * @param direction - Text direction ('ltr' | 'rtl'), defaults to 'ltr'
 *
 * @example
 * ```tsx
 * // LTR (default)
 * <BotyThemeProvider>
 *   <App />
 * </BotyThemeProvider>
 *
 * // RTL
 * <BotyThemeProvider direction="rtl">
 *   <App />
 * </BotyThemeProvider>
 * ```
 */
export function BotyThemeProvider({
  children,
  direction = 'ltr',
}: BotyThemeProviderProps): ReactNode {
  // Memoize theme creation to avoid unnecessary recalculations
  const theme = useMemo(() => createBotyTheme({ direction }), [direction]);

  // Select appropriate cache based on direction
  const cache = direction === 'rtl' ? cacheRtl : cacheLtr;

  return (
    <CacheProvider value={cache}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <div dir={direction}>{children}</div>
      </ThemeProvider>
    </CacheProvider>
  );
}

BotyThemeProvider.displayName = 'BotyThemeProvider';
